<template>
  <p>
    La radiación solar es la principal fuente de energía del sistema
    Tierra-Atmósfera-Océano, por lo que la cuantificación de la cantidad de
    energía que llega a la superficie del planeta es un tema de vital
    importancia. Además, la radiación que emite la superficie terrestre,
    principalmente en el rango del infrarrojo, es importante para cuantificar
    cuanta energía radiativa se libera desde la superficie a la atmósfera. Para
    medir las diferentes componentes del balance radiativo se utilizan
    diferentes instrumentos de medición, los cuales se basan fundamentalmente,
    en el principio de medición de la termopila. Una termopila es la unión en
    serie de determinada cantidad de termopares los cuales, debido a la
    diferencia de temperatura, generan una corriente que es equivalente a la
    cantidad de energía radiante proveniente del Sol o de la Tierra y que
    posteriormente pude ser registrada o medida.
  </p>
  <p>
    Las mediciones de las irradiancias solar y terrestre comprenden las
    diferentes componentes de estas, o sea, la medición de la irradiancia
    directa, difusa, global y reflejada en el caso de la región de onda corta o
    rango visible, e irradiancia global y emitida para el caso de la región
    infrarroja del espectro. El instrumento universal para la medición de la
    irradiancia solar de onda corta es el piranómetro (difusa, global y
    reflejada) mientras que para la medición en la región de onda larga se
    emplea el pirgeómetro (global y emitida). El conjunto de instrumentos
    empleados para la medición de las diferentes componentes antes descritas,
    forman parte de una estación la cual pertenece a la Red para la Línea Base
    de la Radiación Solar (BSRN, Baseline Solar Radiation Network,
    https://bsrn.awi.de/), perteneciente a la Organización Meteorológica Mundial
    (OMM).
  </p>
  <p>
    Esta estación BSRN está compuesta por diferentes instrumentos como se puede
    apreciar en la figura:
  </p>
  <img
    class="thumbnail instrument"
    src="@/assets/images/microfisica/instrumentos/BSRN.webp"
    alt="Estación BSRN"
  />
  <List>
    <ol>
      <li>Pirgeómetro (irradiancia global, onda larga)</li>
      <li>Sombreador</li>
      <li>Piranómetro (irradiancia difusa, onda corta)</li>
      <li>Piranómetro (irradiancia global, onda corta)</li>
      <li>Pirheliómetro (irradiancia directa, onda corta)</li>
      <li>Piranómetro (irradiancia reflejada, onda corta)</li>
      <li>Pirgeómetro (irradiancia</li>
    </ol>
  </List>
  <p>
    <b> Piranómetro </b>
  </p>
  <p>
    El piranómetro es considerado como el instrumento actinométrico universal ya
    que puede ser utilizado para medir todos los flujos radiativos de onda
    corta. Este sensor está formado por una pila termoeléctrica contenida en una
    cápsula protegida por dos semiesferas de cristal, las cuales limitan el
    rango de respuesta del sensor a la radiación de onda corta. El piranómetro
    empleado en la estación de medición de radiación solar es de la marca Kipp
    &amp; Zonen, modelo CMP21 y sus características lo hacen el instrumento por
    excelencia de las estaciones BSRN.
  </p>
  <img
    class="thumbnail instrument"
    src="@/assets/images/microfisica/instrumentos/piranometro.webp"
    alt="Piranómetro"
  />
  <TableUi>
    <thead>
      <tr>
        <th colspan="2">Especificaciones</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>Rango espectral (total)</td>
        <td>285 a 2800 nm</td>
      </tr>
      <tr>
        <td>Sensibilidad</td>
        <td>7 a 14 µV/W/m²</td>
      </tr>
      <tr>
        <td>Tiempo de respuesta</td>
        <td>5 s</td>
      </tr>
      <tr>
        <td>Offset cero A</td>
        <td>&lt; 7 W/m²</td>
      </tr>
      <tr>
        <td>Offset cero B</td>
        <td>&lt; 2 W/m²</td>
      </tr>
      <tr>
        <td>Error direccional (hasta 80 ° a 1000 W/m²)</td>
        <td>&lt; 10 W/m²</td>
      </tr>
      <tr>
        <td>Sensibilidad de dependencia a la temperatura (-20 °C a +50 °C)</td>
        <td>&lt; 1 %</td>
      </tr>
      <tr>
        <td>Rango de temperatura de operación</td>
        <td>-40 °C a +80 °C</td>
      </tr>
      <tr>
        <td>Irradiancia solar máxima</td>
        <td>4000 W/m²</td>
      </tr>
      <tr>
        <td>Campo de visión</td>
        <td>180 °</td>
      </tr>
    </tbody>
  </TableUi>
  <p>
    <b>Pirheliómetro</b>
  </p>
  <p>
    El pirheliómetro el instrumento empleado para la medición de la radiación
    solar directa, o sea, aquella que no es dispersada por ningún componente de
    la atmósfera terrestre. Las características de este instrumento garantizan
    que al sensor llegue solamente la radiación normal a la superficie del
    sensor.
  </p>
  <img
    class="thumbnail instrument"
    src="@/assets/images/microfisica/instrumentos/pirheliometro.webp"
    alt="Pirheliómetro"
  />
  <TableUi>
    <thead>
      <tr>
        <th colspan="2">Especificaciones</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>Rango espectral (total)</td>
        <td>200 a 4000 nm</td>
      </tr>
      <tr>
        <td>Sensibilidad</td>
        <td>7 a 14 µV/W/m²</td>
      </tr>
      <tr>
        <td>Tiempo de respuesta</td>
        <td>&lt; 5 s</td>
      </tr>
      <tr>
        <td>Offset cero B</td>
        <td>&lt; 1 W/m²</td>
      </tr>
      <tr>
        <td>
          Dependencia de la sensibilidad con la temperatura (-20 ºC +50 ºC)
        </td>
        <td>&lt; 0.5 %</td>
      </tr>
      <tr>
        <td>Campo de visión</td>
        <td>5 ±0.2 °</td>
      </tr>
      <tr>
        <td>Tiempo de respuesta</td>
        <td>-40 a +80 s</td>
      </tr>
      <tr>
        <td>No-linealidad</td>
        <td>&lt; 0.2 %</td>
      </tr>
      <tr>
        <td>Irradiancia solar máxima</td>
        <td>4000 W/m²</td>
      </tr>
    </tbody>
  </TableUi>
  <p>
    <b>Pirgeómetro</b>
  </p>
  <p>
    Este instrumento está diseñado para la medición de la irradiancia de onda
    larga, por lo que su rango de medición abarca desde los 4500 nm hasta los
    42000 nm. La cúpula con forma de menisco garantiza que exista un error de
    respuesta direccional mínimo con un campo de visión de 180°. En la estación
    se emplea para la medición de la irradiancia infrarroja de la bóveda celeste
    y de la emitida por la superficie.
  </p>
  <img
    class="thumbnail instrument"
    src="@/assets/images/microfisica/instrumentos/pirgeometro.webp"
    alt="Pirgeómetro"
  />
  <TableUi>
    <thead>
      <tr>
        <th colspan="2">Especificaciones</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>Rango espectral (total)</td>
        <td>4500 a 42000 nm</td>
      </tr>
      <tr>
        <td>Sensibilidad</td>
        <td>5 a 15 µV/W/m²</td>
      </tr>
      <tr>
        <td>Tiempo de respuesta</td>
        <td>18 s</td>
      </tr>
      <tr>
        <td>Offset del calentamiento de la ventana</td>
        <td>&lt; 4 W/m²</td>
      </tr>
      <tr>
        <td>Offset cero B</td>
        <td>&lt; 2 W/m²</td>
      </tr>
      <tr>
        <td>Sensibilidad de dependencia a la temperatura (-20 °C a +50 °C)</td>
        <td>&lt; 1 %</td>
      </tr>
      <tr>
        <td>Rango de temperatura de operación</td>
        <td>-40 a +80 °C</td>
      </tr>
      <tr>
        <td>Límites de irradiancia neta</td>
        <td>-250 a + 250 W/m²</td>
      </tr>
      <tr>
        <td>Campo de visión</td>
        <td>180 °</td>
      </tr>
      <tr>
        <td>No-linealidad</td>
        <td>&lt; 1 %</td>
      </tr>
    </tbody>
  </TableUi>
  <p>
    <b>Seguidor solar</b>
  </p>
  <p>
    El seguidor solar que soporta los instrumentos de la estación es también de
    la marca Kipp &amp; Zonen y corresponde al modelo 2AP. Este es un sistema de
    dos ejes y se constituye en una plataforma de posicionamiento para todo tipo
    de clima y su función es apuntar al sol con la mayor precisión posible los
    instrumentos especializados. Este dispositivo garantiza un posicionamiento
    confiable y asequible para cargas útiles de tamaño pequeño y mediano. En el
    caso de aplicaciones como seguidor solar este equipo opera de forma
    totalmente independiente y desatendida.
  </p>
  <img
    class="thumbnail instrument"
    src="@/assets/images/microfisica/instrumentos/seguidor-solar.webp"
    alt="Seguidor solar"
  />
  <TableUi>
    <thead>
      <tr>
        <th colspan="2">Especificaciones</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>Voltaje de alimentación</td>
        <td>115/230 VAC (±10%), 60/50 Hz</td>
      </tr>
      <tr>
        <td>Potencia</td>
        <td>50VA Standard</td>
      </tr>
      <tr>
        <td>Comunicación</td>
        <td>RS-232</td>
      </tr>
      <tr>
        <td>Motor</td>
        <td>stepping motor</td>
      </tr>
      <tr>
        <td>Precisión</td>
        <td>&lt;0.05°</td>
      </tr>
      <tr>
        <td>Repetibilidad</td>
        <td>&lt;0.01°</td>
      </tr>
      <tr>
        <td>Resolución</td>
        <td>0.0025°</td>
      </tr>
      <tr>
        <td>Velocidad Angular</td>
        <td>hasta 1.8°/seg</td>
      </tr>
      <tr>
        <td>Aceleración Angular</td>
        <td>up to 3.6°/seg 2</td>
      </tr>
      <tr>
        <td>Torque</td>
        <td>40 Nm</td>
      </tr>
      <tr>
        <td>Carga útil</td>
        <td>65 kg balanceada</td>
      </tr>
      <tr>
        <td>Rotación</td>
        <td>
          90°/210º Zenith <br />
          540° Azimuth
        </td>
      </tr>
    </tbody>
  </TableUi>
</template>

<script>
import TableUi from "@/components/ui/objects/TableUi.vue";
export default {
  components: { TableUi },
};
</script>
